/**
 * AccountDetailPanel — bubble-detail panel shown BELOW IndustryPipelineOverview
 * when an Account View bubble is clicked. Uses the shared PipelineDetailShell
 * for parity with Industry / UC / Asset panels: 4×2 KPI grid + StageBreakdown.
 *
 * Backend doesn't accept `accountName` on GetUcPipelineDetail yet, so the fetch
 * is industry-scoped and the shell's `oppFilter` prop narrows opps client-side
 * to this account. Influence KPIs are then derived from that filtered opp set
 * (industry-level stageInsight would over-count).
 */

import type { AccountBubble } from './PipelineMap';
import {
  KPI_INFO_TIPS,
  PipelineDetailShell,
  StatCell,
  deriveKpisFromOpps,
  launchRatioLabel,
} from './PipelineDetailShell';

const FRESHNESS_LABELS: Record<string, string> = {
  active: '活跃',
  cooling: '渐冷',
  stale: '停滞',
};
const FRESHNESS_COLORS: Record<string, string> = {
  active: '#16a34a',
  cooling: '#d97706',
  stale: '#dc2626',
};

export default function AccountDetailPanel({
  industry,
  account,
  pipelineTypeFilter,
  pipelineFilter,
  snapshotDate,
  onClose,
}: {
  industry: string;
  account: AccountBubble;
  pipelineTypeFilter?: string;
  /** Property-tree JSON forwarded so the drill-in matches the bubble filter. */
  pipelineFilter?: string;
  /** Same snapshotDate driving the chart above. */
  snapshotDate?: string;
  onClose: () => void;
}) {
  const fc = FRESHNESS_COLORS[account.freshness] || '#64748b';
  const fl = FRESHNESS_LABELS[account.freshness] || account.freshness;
  const freshnessSuffix =
    account.bestDaysSince !== null ? ` · ${account.bestDaysSince}天前` : '';
  const accountName = account.accountName;

  return (
    <PipelineDetailShell
      title={accountName}
      subtitle={`${industry} · ${account.ucCount} UCs`}
      headerExtra={
        <span
          className="ivt-ucdp__badge"
          style={{ background: fc + '18', color: fc }}
        >
          {fl}
          {freshnessSuffix}
        </span>
      }
      fetch={{ industry }}
      pipelineTypeFilter={pipelineTypeFilter}
      pipelineFilter={pipelineFilter}
      snapshotDate={snapshotDate}
      oppFilter={(opp) => (opp.accountName ?? '') === accountName}
      showUcNameInStages
      loadingLabel={`加载 ${accountName} 明细…`}
      errorHeader="无法加载账户明细"
      onClose={onClose}
      renderKpis={(detail) => {
        // Roll up from the account-scoped opps in the industry response.
        // Industry-level stageInsight would over-count.
        const opps = detail
          ? (detail.stages ?? []).flatMap((s) =>
              (s.opps ?? []).filter((o) => (o.accountName ?? '') === accountName),
            )
          : [];
        const kpis = detail
          ? deriveKpisFromOpps(opps)
          : {
              stronglyTouchedOpps: '—' as const,
              touchedOpps: '—' as const,
              penetrationPct: '—' as const,
            };
        // Pipeline 平均参与深度 sources from the account bubble's
        // influenceDensityOpp so the drawer matches the bubble chart X-axis.
        // The bubble's value is account-scoped (backend rolled it up per
        // account), so it's consistent with the account-filtered opp list.
        const depth = account.influenceDensityOpp.toFixed(2);
        // Top-half KPIs from the SAME account-scoped opps so filter changes
        // (which re-fetch and re-narrow) flow through. Reading from the
        // account bubble snapshot would show stale values.
        const totalArr = opps.reduce((n, o) => n + (o.totalArr ?? 0), 0);
        const launchedArr = opps.reduce(
          (n, o) => n + (o.oppStatus === 'launched' ? (o.totalArr ?? 0) : 0),
          0,
        );
        const arrM = detail ? (totalArr / 1e6).toFixed(2) : '—';
        return (
          <>
            <StatCell label="Industry" value={industry} />
            <StatCell label="Opps" value={detail ? opps.length : '—'} />
            <StatCell label="ARR" value={detail ? `$${arrM}M` : '—'} />
            <StatCell
              label="Launch Ratio"
              value={detail ? launchRatioLabel(launchedArr, totalArr) : '—'}
            />
            <StatCell
              label="Pipeline平均参与深度"
              value={depth}
              info={KPI_INFO_TIPS.pipelineDepth}
            />
            <StatCell
              label="渗透率"
              value={kpis.penetrationPct}
              info={KPI_INFO_TIPS.penetration}
            />
            <StatCell
              label="强相关opps数量"
              value={kpis.stronglyTouchedOpps}
              info={KPI_INFO_TIPS.stronglyTouchedOpps}
            />
            <StatCell
              label="IVT相关opps数量"
              value={kpis.touchedOpps}
              info={KPI_INFO_TIPS.touchedOpps}
            />
          </>
        );
      }}
    />
  );
}
